import type {
  CoreClientQuestionnairesAnswerParams,
  CoreClientQuestionnairesCreateFromResourceParams,
  CoreClientQuestionnairesReadParams,
  CoreCoachQuestionnairesExamplesReadParams,
  CoreCoachQuestionnairesOwnCreateParams,
  CoreCoachQuestionnairesOwnDeleteParams,
  CoreCoachQuestionnairesOwnReadParams,
  CoreCoachQuestionnairesOwnUpdateParams,
  CoreCoachQuestionnairesUserReadParams,
  CoreCoachQuestionnairesUserUpdateParams,
} from '../types/core/methods';
import type {QuestionnaireApiModel, UserQuestionnaireApiModel} from './types';

import {apiRequest} from '../utils/apiRequest';
import {EntityStore} from '../utils/entityStore';
import {expandObj} from '../utils/expand';

class UserQuestionnaireStore<
  T extends UserQuestionnaireApiModel = UserQuestionnaireApiModel,
> extends EntityStore<T, CoreClientQuestionnairesReadParams> {
  answer = this._createMethod<CoreClientQuestionnairesAnswerParams>({
    method: 'client.questionnaires.answer',
  });

  async createFromResource(
    params: CoreClientQuestionnairesCreateFromResourceParams,
  ) {
    const result =
      (await apiRequest<CoreClientQuestionnairesCreateFromResourceParams>({
        method: 'client.questionnaires.create_from_resource',
        params,
      })) as {_expanded: {[key: string]: any}; user_questionnaire: T};

    const {_expanded, user_questionnaire} = result;

    if (params.expand) {
      return expandObj<T, typeof _expanded>(user_questionnaire, {
        expand: params.expand,
        expanded: _expanded,
      });
    } else {
      return user_questionnaire;
    }
  }
}

export class QuestionnaireStore<
  T extends QuestionnaireApiModel = QuestionnaireApiModel,
> extends EntityStore<T> {
  client = new UserQuestionnaireStore('user_questionnaire', {
    fetch: 'client.questionnaires.read',
  });
  clientResult = new EntityStore<
    UserQuestionnaireApiModel,
    CoreCoachQuestionnairesUserReadParams,
    object,
    CoreCoachQuestionnairesUserUpdateParams
  >('user_questionnaire', {
    fetch: 'coach.questionnaires.user.read',
    update: 'coach.questionnaires.user.update',
  });
  coach = new EntityStore<
    T,
    CoreCoachQuestionnairesOwnReadParams,
    CoreCoachQuestionnairesOwnCreateParams,
    CoreCoachQuestionnairesOwnUpdateParams,
    CoreCoachQuestionnairesOwnDeleteParams
  >('questionnaire', {
    create: 'coach.questionnaires.own.create',
    delete: 'coach.questionnaires.own.delete',
    fetch: 'coach.questionnaires.own.read',
    update: 'coach.questionnaires.own.update',
  });
  template = new EntityStore<T, CoreCoachQuestionnairesExamplesReadParams>(
    'questionnaire',
    {
      fetch: 'coach.questionnaires.examples.read',
    },
  );
}

export const questionnaireStore = new QuestionnaireStore('questionnaire');
